import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Statistic, Spin, Space, DatePicker, Alert, Tabs } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  RiseOutlined,
  FundOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Cell, ReferenceLine,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import Inventory from './pw/Inventory';
import CreditNotes from './pw/CreditNotes';
import api from '../libs/api';

const { RangePicker } = DatePicker;

// Fiscal year helpers
function getFiscalQuarter(d) {
  const m = d.month();
  if (m >= 6 && m <= 8) return { q: 1, start: d.year(), startM: 6, endM: 8 };
  if (m >= 9 && m <= 11) return { q: 2, start: d.year(), startM: 9, endM: 11 };
  if (m >= 0 && m <= 2) return { q: 3, start: d.year(), startM: 0, endM: 2 };
  return { q: 4, start: d.year(), startM: 3, endM: 5 };
}

function getFiscalYearStart(d) {
  return d.month() >= 6
    ? dayjs().year(d.year()).month(6).startOf('month')
    : dayjs().year(d.year() - 1).month(6).startOf('month');
}

const now = dayjs();
const curQ = getFiscalQuarter(now);
const prevQDate = dayjs().month(curQ.startM).startOf('month').subtract(1, 'day');
const prevQ = getFiscalQuarter(prevQDate);
const fyStart = getFiscalYearStart(now);
const fyEnd = dayjs(fyStart).add(1, 'year').subtract(1, 'day').endOf('day');
const lastFyStart = fyStart.subtract(1, 'year');
const lastFyEnd = fyStart.subtract(1, 'day').endOf('day');

const DATE_PRESETS = [
  { label: 'This Month', value: [now.startOf('month'), now.endOf('month')] },
  { label: 'Last Month', value: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: 'This Quarter', value: [dayjs().year(curQ.start).month(curQ.startM).startOf('month'), dayjs().year(curQ.start).month(curQ.endM).endOf('month')] },
  { label: 'Last Quarter', value: [dayjs().year(prevQ.start).month(prevQ.startM).startOf('month'), dayjs().year(prevQ.start).month(prevQ.endM).endOf('month')] },
  { label: 'Last 6 Months', value: [now.subtract(6, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: `This Year (FY${fyStart.year()}/${fyStart.year() + 1})`, value: [fyStart, fyEnd] },
  { label: `Last Year (FY${lastFyStart.year()}/${lastFyStart.year() + 1})`, value: [lastFyStart, lastFyEnd] },
];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

const numFmt = (v) => (v != null ? Number(v).toLocaleString('en-AU', { maximumFractionDigits: 2 }) : '-');

/**
 * How long stock sits before it sells, banded: under 30 days, 30 to 100, and
 * beyond. Each boundary belongs to the slower band, so 30 days exactly is
 * amber and 100 exactly is still amber — red starts past it.
 */
const DAYS_BANDS = [
  { label: '<30d', colour: '#05A677', holds: (d) => d < 30 },
  { label: '30-100d', colour: '#FAB005', holds: (d) => d <= 100 },
  { label: '>100d', colour: '#FA5252', holds: () => true },
];

export default function Distribution() {
  const [dateRange, setDateRange] = useState([now.startOf('month'), now.endOf('month')]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [arData, setArData] = useState([]);
  const [overdueData, setOverdueData] = useState([]);
  // What the twelve-month window leaves out, so it can be named rather than
  // quietly dropped — see the filter below.
  const [arExcluded, setArExcluded] = useState({ count: 0, value: 0 });
  const [arLoading, setArLoading] = useState(false);
  const [arError, setArError] = useState(null);

  const fetchData = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    setLoading(true);
    setApiError(null);
    try {
      const res = await api.get('/api/distribution/summary', {
        params: {
          start: range[0].format('YYYY-MM-DD'),
          end: range[1].format('YYYY-MM-DD'),
        },
      });
      setData(res.data.data);
    } catch (err) {
      const resp = err.response;
      if (resp && resp.data?.code === 'UNLEASHED_API_ERROR') {
        setApiError(resp.data.message);
      } else if (resp && resp.status >= 500) {
        setApiError('Unable to connect to Unleashed API. Please check server configuration.');
      } else if (!resp) {
        setApiError('Network error — unable to reach the server.');
      }
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(dateRange); }, [dateRange, fetchData]);

  const s = data?.stats || {};
  const salesOrders = data?.salesOrders || [];
  const stockOnHandRaw = data?.stockOnHand || [];

  // Show items with any stock (qtyOnHand, availableQty, allocatedQty, or onPurchase > 0)
  const stockOnHand = stockOnHandRaw
    .filter(r => r.qtyOnHand !== 0 || r.availableQty !== 0 || r.allocatedQty !== 0 || r.onPurchase !== 0);

  // Fetch PW Outstanding AR (Xero) — aggregate by customer; also compute Overdue (dueDate < today).
  useEffect(() => {
    let cancelled = false;
    setArLoading(true);
    setArError(null);
    api.get('/api/financial-planning/invoices', { params: { entity: 'pw' } })
      .then(res => {
        if (cancelled) return;
        const ar = res.data?.data?.ar || [];
        const today = new Date().toISOString().slice(0, 10);
        /**
         * Only invoices raised in the last twelve months.
         *
         * Anything older is still owed, and is usually the debt worth worrying
         * about most, so the amount left out is counted and shown on both
         * cards rather than disappearing from a chart about money owed.
         *
         * An invoice with no date is kept. It cannot be shown to be old, and
         * dropping it would lose real money to a missing field.
         */
        const cutoff = dayjs().subtract(12, 'month').format('YYYY-MM-DD');
        const totalMap = {};
        const overdueMap = {};
        let exCount = 0;
        let exValue = 0;
        for (const inv of ar) {
          const amt = inv.amountDue || 0;
          if (inv.date && inv.date.slice(0, 10) < cutoff) {
            exCount += 1;
            exValue += amt;
            continue;
          }
          const name = inv.contactName || 'Unknown';
          totalMap[name] = (totalMap[name] || 0) + amt;
          if (inv.dueDate && inv.dueDate.slice(0, 10) < today) {
            overdueMap[name] = (overdueMap[name] || 0) + amt;
          }
        }
        setArExcluded({ count: exCount, value: exValue });
        const toArr = (map) => Object.entries(map)
          .map(([name, value]) => ({ name, value }))
          .filter(e => e.value > 0)
          .sort((a, b) => b.value - a.value);
        setArData(toArr(totalMap));
        setOverdueData(toArr(overdueMap));
      })
      .catch(err => {
        if (cancelled) return;
        setArError(err.response?.data?.message || 'Failed to load AR data');
      })
      .finally(() => { if (!cancelled) setArLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Per-product Days Sales of Inventory: (inventory $ / period COGS $) × period days.
  // Products with no sales in the period have undefined DSI (∞) and are skipped.
  const turnoverData = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];
    const periodDays = Math.max(1, dateRange[1].diff(dateRange[0], 'day') + 1);
    const cogsByProduct = {};
    for (const o of salesOrders) {
      if (o.isCharge) continue;
      const code = o.productCode || 'Unknown';
      cogsByProduct[code] = (cogsByProduct[code] || 0) + (parseFloat(o.cost) || 0);
    }
    const points = [];
    for (const stock of stockOnHand) {
      const inv = parseFloat(stock.totalCost) || 0;
      if (inv <= 0) continue;                                  // no inventory → not on chart
      const cogs = cogsByProduct[stock.productCode] || 0;
      if (cogs <= 0) continue;                                 // no sales in period → DSI undefined
      const daysToSell = (inv / cogs) * periodDays;
      points.push({
        code: stock.productCode,
        description: stock.productDescription,
        group: stock.productGroup,
        inventory: inv,
        qtyOnHand: stock.qtyOnHand,
        daysToSell,
      });
    }
    return points;
  }, [salesOrders, stockOnHand, dateRange]);

  // Range over a numeric field of turnoverData (used for both log-domain and log-ticks).
  const rangeOver = (key) => {
    let lo = Infinity, hi = 0;
    for (const p of turnoverData) {
      const v = p[key];
      if (v > 0) {
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    return isFinite(lo) && hi > 0 ? [lo, hi] : null;
  };

  // Snap a value to the next-lower / next-higher power of 10.
  const floorLog10 = (v) => Math.pow(10, Math.floor(Math.log10(Math.max(v, 1e-9))));
  const ceilLog10  = (v) => Math.pow(10, Math.ceil (Math.log10(Math.max(v, 1e-9))));
  // Powers of 10 covering [lo, hi] inclusive after snapping each end.
  const log10Ticks = (lo, hi) => {
    const e0 = Math.round(Math.log10(floorLog10(lo)));
    const e1 = Math.round(Math.log10(ceilLog10(hi)));
    const out = [];
    for (let e = e0; e <= e1; e++) out.push(Math.pow(10, e));
    return out;
  };


  // Chart data: Aggregate Sales Orders by Customer
  const chartDataByCustomer = useMemo(() => {
    const customerMap = {};
    salesOrders.forEach(order => {
      if (order.isCharge) return; // Skip charge lines
      const customer = order.customer || 'Unknown';
      const sell = parseFloat(order.sell) || 0;
      if (!customerMap[customer]) customerMap[customer] = 0;
      customerMap[customer] += sell;
    });
    const sorted = Object.entries(customerMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 customers
    const total = sorted.reduce((sum, item) => sum + item.value, 0);
    return sorted.map(item => ({ ...item, percentage: total > 0 ? (item.value / total) * 100 : 0 }));
  }, [salesOrders]);

  // Chart data: Aggregate Sales Orders by Product Code
  const chartDataByProduct = useMemo(() => {
    const productMap = {};
    salesOrders.forEach(order => {
      if (order.isCharge) return; // Skip charge lines
      const product = order.productCode || 'Unknown';
      const sell = parseFloat(order.sell) || 0;
      if (!productMap[product]) productMap[product] = 0;
      productMap[product] += sell;
    });
    const sorted = Object.entries(productMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 products
    const total = sorted.reduce((sum, item) => sum + item.value, 0);
    return sorted.map(item => ({ ...item, percentage: total > 0 ? (item.value / total) * 100 : 0 }));
  }, [salesOrders]);

  /**
   * Margin against size, by customer and by sales order.
   *
   * Gross profit is worked out exactly as the summary card above does it:
   * sell minus cost over sell, across the lines that actually sell something.
   * Charge lines are left out on both sides — they carry freight and fees with
   * no cost of their own, so counting them would lift every margin by a
   * different amount depending on how much freight that customer happened to
   * be billed.
   *
   * A group with no sales value has no margin to plot and is dropped rather
   * than drawn at zero, which would invent a cluster on the axis that does not
   * mean anything. Its cost still counts towards the overall line — see below.
   */
  const gpScatter = useMemo(() => {
    const build = (keyOf) => {
      const acc = {};
      for (const order of salesOrders) {
        if (order.isCharge) continue;
        const key = keyOf(order) || 'Unknown';
        const sell = parseFloat(order.sell) || 0;
        const cost = parseFloat(order.cost) || 0;
        if (!acc[key]) {
          acc[key] = {
            name: key, sales: 0, cost: 0, lines: 0,
            customer: order.customer || '-',
            // Distinct orders behind the group. A set because one order
            // contributes many lines and must still only be counted once.
            orders: new Set(),
          };
        }
        const g = acc[key];
        g.sales += sell;
        g.cost += cost;
        g.lines += 1;
        if (order.orderNumber) g.orders.add(order.orderNumber);
      }
      const all = Object.values(acc);
      const points = all
        .filter((x) => x.sales > 0)
        .map((x) => {
          const soCount = x.orders.size;
          return {
            name: x.name,
            customer: x.customer,
            sales: x.sales,
            cost: x.cost,
            lines: x.lines,
            soCount,
            avgSo: soCount > 0 ? x.sales / soCount : 0,
            profit: x.sales - x.cost,
            gpPct: ((x.sales - x.cost) / x.sales) * 100,
          };
        })
        .sort((a, b) => b.sales - a.sales);
      /**
       * The overall margin is taken across every line, including the groups
       * that cannot be plotted.
       *
       * A group that sold nothing has no percentage to place on the axis, but
       * it can still have carried cost — a line priced at zero against real
       * stock. Leaving that cost out of the average would put the reference
       * line above the margin printed at the top of this page, and a chart
       * that quietly disagrees with the headline is worse than one that omits
       * a point.
       */
      const sales = all.reduce((t, x) => t + x.sales, 0);
      const cost = all.reduce((t, x) => t + x.cost, 0);

      // Both axes are logarithmic, so each domain is snapped out to whole
      // powers of ten and the ticks sit on them.
      const spanOf = (field, fallback) => {
        let lo = Infinity;
        let hi = 0;
        for (const x of points) {
          const v = x[field];
          if (v > 0) { if (v < lo) lo = v; if (v > hi) hi = v; }
        }
        return (isFinite(lo) && hi > 0)
          ? { domain: [floorLog10(lo), ceilLog10(hi)], ticks: log10Ticks(lo, hi) }
          : fallback;
      };
      const x = spanOf('sales', {
        domain: [1, 1_000_000],
        ticks: [1, 10, 100, 1000, 10_000, 100_000, 1_000_000],
      });
      /**
       * The margin axis is bounded to two decades below its top.
       *
       * Left to the data it would not be. Four orders in a recent quarter
       * priced within rounding of their cost, which puts the smallest positive
       * margin at about a millionth of a percent and stretches the axis across
       * eight decades — compressing the eight hundred orders that matter into
       * the top corner. Two decades is enough to separate a three percent
       * order from a thirty percent one, which is the comparison being made.
       */
      const yRaw = spanOf('gpPct', { domain: [1, 100], ticks: [1, 10, 100] });
      const yTop = yRaw.domain[1];
      const yFloor = Math.max(yRaw.domain[0], yTop / 100);
      const y = { domain: [yFloor, yTop], ticks: yRaw.ticks.filter((t) => t >= yFloor) };

      /**
       * A logarithmic axis has no room for zero, for a loss, or for anything
       * under the floor above.
       *
       * Dropping those points would be the quiet option and the wrong one:
       * they are the loss-making customers and orders, which is exactly what
       * someone reads this chart to find. They are drawn instead along the
       * bottom of the scale as crosses, carrying their real percentage in the
       * tooltip, so the axis stays honest while the point stays visible.
       */
      const onScale = points.filter((pt) => pt.gpPct >= yFloor);
      /**
       * The pinned points carry the floor as their gpPct, not their own value.
       *
       * Recharts reads a scatter's y from the axis's dataKey, never from the
       * one on the series, so a point left holding minus two hundred percent
       * is still handed to the log scale. That returns NaN, and a single NaN
       * stops the axis building a domain at all — which empties the chart
       * rather than misplacing one mark. The real figure moves to realGpPct
       * for the tooltip to show.
       */
      const offScale = points
        .filter((pt) => pt.gpPct < yFloor)
        .map((pt) => ({ ...pt, gpPct: yFloor, realGpPct: pt.gpPct, offScale: true }));

      return {
        points,
        onScale,
        offScale,
        avgPct: sales > 0 ? ((sales - cost) / sales) * 100 : 0,
        dropped: all.length - points.length,
        domain: x.domain,
        ticks: x.ticks,
        yDomain: y.domain,
        yTicks: y.ticks,
      };
    };
    return { byCustomer: build((o) => o.customer), bySo: build((o) => o.orderNumber) };
  }, [salesOrders]);

  /**
   * One control, two tabs, one piece of state.
   *
   * Sales and Credit Notes are both period questions answered on the range
   * picked here, so the same picker is rendered in each and switching between
   * them keeps the period rather than making it be chosen twice. It sits
   * inside the tabs rather than above them because Inventory is a position
   * and has no period to pick — see the note on the tabs below.
   */
  const periodToolbar = (
    <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
      <Space wrap size="middle" align="center">
        <CalendarOutlined style={{ color: '#54A2CC' }} />
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          format="DD MMM YYYY"
          size="small"
          allowClear={false}
          presets={DATE_PRESETS}
        />
      </Space>
    </Card>
  );

  return (
    <PageLayout>
      <div className="page-header">
        <h1>PW Renewable</h1>
        <p>Sales and purchasing from Unleashed; stock and warehouse cost by site</p>
      </div>
      {/*
        * Three tabs, because they do not all answer the same shape of
        * question.
        *
        * Sales and Credit Notes are both period questions and share the range
        * picker, but not a basis: a sale is dated when the order completed,
        * a credit when it was raised, so the two tabs will not add up for the
        * same month and are not presented as though they should.
        *
        * Inventory is a position — what is held right now, where, and what
        * the 3PL charges to hold it. A date range means nothing to it and a
        * warehouse selector means nothing to a completed sales order, which
        * is why no single toolbar sits above all three.
        */}
      <Tabs
        defaultActiveKey="sales"
        items={[{
          key: 'sales',
          label: 'Sales',
          children: (
            <>

      {periodToolbar}

      {apiError && (
        <Alert
          type="error"
          showIcon
          message="Unleashed API Connection Failed"
          description={apiError}
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      <Spin spinning={loading}>
        {/* KPI Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Card>
              <DollarOutlined style={{ color: '#54A2CC', fontSize: 22, marginBottom: 8 }} />
              <Statistic
                title="Total Sell"
                value={s.totalSell}
                formatter={(v) => fmt(v)}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {s.transactionCount || 0} lines, {numFmt(s.totalQty)} qty
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Card>
              <FundOutlined style={{ color: '#0081CF', fontSize: 22, marginBottom: 8 }} />
              <Statistic
                title="Total Charge"
                value={s.totalCharge}
                formatter={(v) => fmt(v)}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                surcharges &amp; fees
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Card>
              <DollarOutlined style={{ color: '#05A677', fontSize: 22, marginBottom: 8 }} />
              <Statistic
                title="Total Turnover"
                value={s.totalTurnover}
                formatter={(v) => fmt(v)}
                valueStyle={{ color: '#05A677' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                sell + charge
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Card>
              <ShoppingCartOutlined style={{ color: '#FA5252', fontSize: 22, marginBottom: 8 }} />
              <Statistic
                title="Total Cost"
                value={s.totalCost}
                formatter={(v) => fmt(v)}
                valueStyle={{ color: '#FA5252' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                avg landed cost
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Card>
              <RiseOutlined style={{ color: (s.totalProfit ?? 0) >= 0 ? '#05A677' : '#FA5252', fontSize: 22, marginBottom: 8 }} />
              <Statistic
                title="Total Profit"
                value={s.totalProfit}
                formatter={(v) => fmt(v)}
                valueStyle={{ color: (s.totalProfit ?? 0) >= 0 ? '#05A677' : '#FA5252' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                sell &minus; cost
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Card>
              <PieChartOutlined style={{ color: (s.overallMarginPct ?? 0) >= 0 ? '#05A677' : '#FA5252', fontSize: 22, marginBottom: 8 }} />
              <Statistic
                title="Overall Margin"
                value={s.overallMarginPct != null ? s.overallMarginPct.toFixed(2) : 0}
                suffix="%"
                valueStyle={{ color: (s.overallMarginPct ?? 0) >= 0 ? '#05A677' : '#FA5252' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                profit / sell
              </div>
            </Card>
          </Col>
        </Row>

        {/* Charts Section */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {/* Bar Chart: Sales by Customer */}
          <Col xs={24} lg={12}>
            <Card title="Sales by Customer (Top 10)" size="small">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartDataByCustomer} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={(value) => fmt(value)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(value, name, props) => [
                      `${fmt(value)} (${props.payload.percentage.toFixed(1)}%)`,
                      'Total Sell'
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Total Sell" fill="#54A2CC" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Bar Chart: Sales by Product Code */}
          <Col xs={24} lg={12}>
            <Card title="Sales by Product Code (Top 10)" size="small">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartDataByProduct} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={(value) => fmt(value)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(value, name, props) => [
                      `${fmt(value)} (${props.payload.percentage.toFixed(1)}%)`,
                      'Total Sell'
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Total Sell" fill="#05A677" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Margin against size, under the two bars the groups come from */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {[
            { key: 'byCustomer', title: 'GP% vs Total Sales by Customer', label: 'Customer' },
            { key: 'bySo', title: 'GP% vs Total Sales by SO#', label: 'Order' },
          ].map(({ key, title, label }) => {
            const {
              points, onScale, offScale, avgPct, dropped, domain, ticks, yDomain, yTicks,
            } = gpScatter[key];
            return (
              <Col xs={24} lg={12} key={key}>
                <Card
                  title={title}
                  size="small"
                  extra={(
                    <span style={{ fontSize: 11, color: '#999' }}>
                      {numFmt(points.length)} {label.toLowerCase()}
                      {points.length === 1 ? '' : 's'} · log scales · dashed = overall {avgPct.toFixed(1)}%
                      {offScale.length > 0 && ` · ${offScale.length} below the axis floor, pinned as crosses`}
                      {dropped > 0 && ` · ${dropped} with no sales value not shown`}
                    </span>
                  )}
                >
                  {points.length ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          dataKey="sales"
                          name="Total Sales"
                          scale="log"
                          domain={domain}
                          ticks={ticks}
                          label={{ value: 'Total Sales (log)', position: 'insideBottom', offset: -10, fontSize: 12 }}
                          tickFormatter={fmt}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          type="number"
                          dataKey="gpPct"
                          name="GP%"
                          scale="log"
                          domain={yDomain}
                          ticks={yTicks}
                          label={{ value: 'GP % (log)', angle: -90, position: 'insideLeft', fontSize: 12 }}
                          tickFormatter={(v) => (v < 1 ? `${v}%` : `${Math.round(v)}%`)}
                          tick={{ fontSize: 11 }}
                        />
                        <ZAxis type="number" range={[55, 55]} />
                        {/* Break-even cannot be drawn on a log axis — zero is not
                            on it. The crosses along the floor are where it went. */}
                        {avgPct > 0 && (
                          <ReferenceLine y={avgPct} stroke="#8c8c8c" strokeDasharray="4 4" />
                        )}
                        <Tooltip
                          cursor={{ strokeDasharray: '3 3' }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload;
                            return (
                              <div style={{
                                background: '#fff', padding: 10, border: '1px solid #d9d9d9',
                                fontSize: 12, lineHeight: 1.6, maxWidth: 260,
                              }}
                              >
                                <div><b>{key === 'bySo' ? `SO ${d.name}` : d.name}</b></div>
                                {key === 'bySo' && (
                                  <div style={{ color: '#666' }}>{d.customer}</div>
                                )}
                                <div>Total Sales: <b>{fmt(d.sales)}</b></div>
                                <div>Gross Profit: <b>{fmt(d.profit)}</b></div>
                                {(() => {
                                  // A pinned point's gpPct was replaced by the
                                  // axis floor so the log scale could take it.
                                  const pct = d.offScale ? d.realGpPct : d.gpPct;
                                  return (
                                    <div>
                                      GP%: <b style={{ color: pct > 0 ? '#05A677' : '#FA5252' }}>
                                        {pct.toFixed(1)}%
                                      </b>
                                      {d.offScale && (
                                        <span style={{ color: '#FA5252' }}> · below the axis floor</span>
                                      )}
                                    </div>
                                  );
                                })()}
                                {key === 'byCustomer' && (
                                  <div>
                                    Orders: <b>{numFmt(d.soCount)}</b>
                                    {' · avg '}<b>{fmt(d.avgSo)}</b>/SO
                                  </div>
                                )}
                                <div style={{ color: '#888' }}>
                                  {d.lines} line{d.lines === 1 ? '' : 's'}
                                  {' · '}
                                  {(((d.offScale ? d.realGpPct : d.gpPct) - avgPct) >= 0 ? '+' : '')}
                                  {((d.offScale ? d.realGpPct : d.gpPct) - avgPct).toFixed(1)} pts
                                  {' vs overall'}
                                </div>
                              </div>
                            );
                          }}
                        />
                        <Scatter name={label} data={onScale}>
                          {onScale.map((d) => (
                            // Green at or above the overall margin, amber below.
                            <Cell
                              key={d.name}
                              fill={d.gpPct >= avgPct ? '#05A677' : '#FAB005'}
                              fillOpacity={0.75}
                            />
                          ))}
                        </Scatter>
                        {offScale.length > 0 && (
                          <Scatter name="Below floor" data={offScale} fill="#FA5252" shape="cross" />
                        )}
                      </ScatterChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                      No sales in this period
                    </div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>


        {/* AR analytics row: Outstanding | Overdue */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Outstanding AR by Customer (Xero — PW Renewable)"
              size="small"
              extra={(
                <span style={{ fontSize: 11, color: '#999' }}>
                  last 12 months · top 20
                  {arExcluded.count > 0 && (
                    <span style={{ color: '#d48806' }}>
                      {' · '}{arExcluded.count} older invoice{arExcluded.count === 1 ? '' : 's'}
                      {' worth '}{fmt(arExcluded.value)}{' excluded'}
                    </span>
                  )}
                </span>
              )}
            >
              {arError && (
                <Alert type="warning" message={arError} showIcon style={{ marginBottom: 8 }} />
              )}
              <Spin spinning={arLoading}>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={arData.slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 110, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={160} interval={0} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="value" name="Outstanding AR" fill="#FA5252" />
                  </BarChart>
                </ResponsiveContainer>
              </Spin>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title="Overdue by Customer (dueDate < today)"
              size="small"
              extra={(
                <span style={{ fontSize: 11, color: '#999' }}>
                  last 12 months · top 20
                  {arExcluded.count > 0 && (
                    <span style={{ color: '#d48806' }}>
                      {' · '}{arExcluded.count} older invoice{arExcluded.count === 1 ? '' : 's'}
                      {' worth '}{fmt(arExcluded.value)}{' excluded'}
                    </span>
                  )}
                </span>
              )}
            >
              <Spin spinning={arLoading}>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={overdueData.slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 110, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={160} interval={0} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="value" name="Overdue" fill="#cf1322" />
                  </BarChart>
                </ResponsiveContainer>
              </Spin>
            </Card>
          </Col>
        </Row>
      </Spin>
            </>
          ),
        }, {
          key: 'credit-notes',
          label: 'Credit Notes',
          children: (
            <>
              {periodToolbar}
              <CreditNotes dateRange={dateRange} />
            </>
          ),
        }, {
          key: 'inventory',
          label: 'Inventory',
          children: <Inventory />,
        }]}
      />
    </PageLayout>
  );
}
